import React, { useState, useEffect } from 'react';
import { X, Crown, ShoppingCart, Sparkles, Users, Gift } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API_URL from '../config/API_URL';
import { useAuth } from '../context/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ selectedPackage, tokenType, onSuccess, onCancel, showToast }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Package details for both story and share tokens
  const storyPackageDetails = {
    '5_tokens': { tokens: 5, price: 15, display: '$15.00' },
    '12_tokens': { tokens: 12, price: 30, display: '$30.00' }
  };

  const sharePackageDetails = {
    '4_tokens': { tokens: 4, price: 15, display: '$15.00' },
    '10_tokens': { tokens: 10, price: 30, display: '$30.00' }
  };

  const details = tokenType === 'share' 
    ? sharePackageDetails[selectedPackage]
    : storyPackageDetails[selectedPackage];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Determine which endpoint to call
      const endpoint = tokenType === 'share' 
        ? `${API_URL}/api/stripe/purchase-share-tokens`
        : `${API_URL}/api/stripe/purchase-story-tokens`;

      // Create payment intent
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ package: selectedPackage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }

      // Confirm payment
      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement)
        }
      });

      if (result.error) {
        setError(result.error.message);
        showToast(result.error.message, 'error');
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          const tokenName = tokenType === 'share' ? 'share tokens' : 'story tokens';
          showToast(`Successfully purchased ${details.tokens} ${tokenName}!`, 'success');
          onSuccess();
        }
      }
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const tokenIcon = tokenType === 'share' ? <Users size={24} /> : <Sparkles size={24} />;
  const tokenName = tokenType === 'share' ? 'Share Tokens' : 'Story Tokens';
  const tokenDescription = tokenType === 'share'
    ? 'Share premium stories with parent families'
    : 'Unlock premium stories from our exclusive library';

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{
        background: tokenType === 'share' 
          ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
          : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        padding: '1.5rem',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
        border: `2px solid ${tokenType === 'share' ? '#3b82f6' : '#fbbf24'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{
            background: tokenType === 'share'
              ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
              : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white',
            boxShadow: `0 4px 6px ${tokenType === 'share' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`
          }}>
            {details.tokens}
          </div>
          <div>
            <h3 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: tokenType === 'share' ? '#1e3a8a' : '#92400e', 
              margin: 0 
            }}>
              {details.tokens} {tokenName}
            </h3>
            <p style={{ 
              fontSize: '1.25rem', 
              color: tokenType === 'share' ? '#1e40af' : '#b45309', 
              margin: 0 
            }}>
              {details.display}
            </p>
          </div>
        </div>
        <p style={{ 
          fontSize: '0.875rem', 
          color: tokenType === 'share' ? '#1e3a8a' : '#92400e', 
          margin: 0 
        }}>
          {tokenDescription}
        </p>
      </div>

      <div style={{
        background: 'white',
        padding: '1rem',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        marginBottom: '1rem'
      }}>
        <label style={{
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: '600',
          color: '#374151',
          marginBottom: '0.5rem'
        }}>
          Card Information
        </label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1f2937',
                '::placeholder': {
                  color: '#9ca3af',
                },
              },
              invalid: {
                color: '#ef4444',
              },
            },
          }}
        />
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          padding: '0.75rem',
          marginBottom: '1rem',
          color: '#991b1b',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1,
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: '1px solid #d1d5db',
            background: 'white',
            color: '#374151',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          style={{
            flex: 1,
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: loading ? '#9ca3af' : (
              tokenType === 'share'
                ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                : 'linear-gradient(135deg, #fbbf24, #f59e0b)'
            ),
            color: 'white',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: (!stripe || loading) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            boxShadow: tokenType === 'share' 
              ? '0 4px 6px rgba(59, 130, 246, 0.3)'
              : '0 4px 6px rgba(251, 191, 36, 0.3)'
          }}
        >
          {loading ? (
            'Processing...'
          ) : (
            <>
              <ShoppingCart size={20} />
              Purchase {details.display}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export const TokenPurchaseModal = ({ isOpen, onClose, showToast, onPurchaseComplete }) => {
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [tokenType, setTokenType] = useState(null);

  const isGrandparent = user?.role !== 'parent';

  // Story token packages for parents
  const storyPackages = [
    {
      id: '5_tokens',
      tokens: 5,
      price: '$15.00',
      popular: false
    },
    {
      id: '12_tokens',
      tokens: 12,
      price: '$30.00',
      popular: true,
      savings: 'Save $6'
    }
  ];

  // Share token packages for grandparents
  const sharePackages = [
    {
      id: '4_tokens',
      tokens: 4,
      price: '$15.00',
      popular: false
    },
    {
      id: '10_tokens',
      tokens: 10,
      price: '$30.00',
      popular: true,
      savings: 'Best Deal'
    }
  ];

  if (!isOpen) return null;

  const handleSuccess = () => {
    setSelectedPackage(null);
    setTokenType(null);
    onPurchaseComplete();
    onClose();
  };

  const handleCancel = () => {
    setSelectedPackage(null);
    setTokenType(null);
  };

  const handlePackageSelect = (packageId, type) => {
    setSelectedPackage(packageId);
    setTokenType(type);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '1rem',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '1.5rem',
          borderTopLeftRadius: '1rem',
          borderTopRightRadius: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ShoppingCart size={28} color="white" />
            </div>
            <div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 'bold',
                color: 'white',
                margin: 0,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                Purchase Tokens
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>
                {isGrandparent ? 'Story & Share Tokens' : 'Story Tokens'}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedPackage(null);
              setTokenType(null);
              onClose();
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={24} color="white" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {!selectedPackage ? (
            <>
              {/* Story Tokens Section */}
              <div style={{ marginBottom: isGrandparent ? '2rem' : 0 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem', 
                  marginBottom: '1rem' 
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Sparkles size={20} color="white" />
                  </div>
                  <h3 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold', 
                    color: '#1f2937',
                    margin: 0
                  }}>
                    Story Tokens
                  </h3>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                  Unlock premium stories from our exclusive library
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  {storyPackages.map(pkg => (
                    <div
                      key={pkg.id}
                      onClick={() => handlePackageSelect(pkg.id, 'story')}
                      style={{
                        border: '2px solid',
                        borderColor: pkg.popular ? '#fbbf24' : '#e5e7eb',
                        borderRadius: '1rem',
                        padding: '1.25rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: pkg.popular ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : 'white',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(251, 191, 36, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {pkg.popular && (
                        <div style={{
                          position: 'absolute',
                          top: '-10px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                          color: 'white',
                          padding: '0.2rem 0.75rem',
                          borderRadius: '1rem',
                          fontSize: '0.625rem',
                          fontWeight: '700',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <Crown size={10} /> {pkg.savings}
                        </div>
                      )}

                      <div style={{
                        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        borderRadius: '50%',
                        width: '60px',
                        height: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 0.75rem',
                        boxShadow: '0 4px 6px rgba(251, 191, 36, 0.3)',
                        border: '3px solid white'
                      }}>
                        <span style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: 'white'
                        }}>
                          {pkg.tokens}
                        </span>
                      </div>

                      <p style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#f59e0b',
                        margin: '0.5rem 0'
                      }}>
                        {pkg.price}
                      </p>

                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280'
                      }}>
                        ${(parseFloat(pkg.price.replace('$', '')) / pkg.tokens).toFixed(2)} per token
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Share Tokens Section - Only for Grandparents */}
              {isGrandparent && (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem', 
                    marginBottom: '1rem' 
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Gift size={20} color="white" />
                    </div>
                    <h3 style={{ 
                      fontSize: '1.25rem', 
                      fontWeight: 'bold', 
                      color: '#1f2937',
                      margin: 0
                    }}>
                      Share Tokens
                    </h3>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                    Share premium stories with parent families
                  </p>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem'
                  }}>
                    {sharePackages.map(pkg => (
                      <div
                        key={pkg.id}
                        onClick={() => handlePackageSelect(pkg.id, 'share')}
                        style={{
                          border: '2px solid',
                          borderColor: pkg.popular ? '#3b82f6' : '#e5e7eb',
                          borderRadius: '1rem',
                          padding: '1.25rem',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          background: pkg.popular ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' : 'white',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {pkg.popular && (
                          <div style={{
                            position: 'absolute',
                            top: '-10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: 'white',
                            padding: '0.2rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <Crown size={10} /> {pkg.savings}
                          </div>
                        )}

                        <div style={{
                          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          borderRadius: '50%',
                          width: '60px',
                          height: '60px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 0.75rem',
                          boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)',
                          border: '3px solid white'
                        }}>
                          <span style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: 'white'
                          }}>
                            {pkg.tokens}
                          </span>
                        </div>

                        <p style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: '#2563eb',
                          margin: '0.5rem 0'
                        }}>
                          {pkg.price}
                        </p>

                        <p style={{
                          fontSize: '0.75rem',
                          color: '#6b7280'
                        }}>
                          ${(parseFloat(pkg.price.replace('$', '')) / pkg.tokens).toFixed(2)} per token
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: '1px solid #86efac',
                borderRadius: '0.75rem',
                padding: '1rem',
                marginTop: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <Sparkles size={20} color="#059669" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#065f46', fontWeight: '600', marginBottom: '0.25rem' }}>
                      Tokens never expire!
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#047857', margin: 0 }}>
                      All tokens roll over month to month and never expire. Use them whenever you want!
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Elements stripe={stripePromise}>
              <CheckoutForm
                selectedPackage={selectedPackage}
                tokenType={tokenType}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
                showToast={showToast}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};